import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../../components/navBar';
import LoadingAnimation from '../../components/LoadingAnimation';
import { format } from 'date-fns';

export default function CoachDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coachTitles, setCoachTitles] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const fetchCoachData = async () => {
      try {
        // Fetch coach details
        const coachResponse = await axios.get(`${process.env.REACT_APP_API_URL}/coaches/${id}`);
        // Extract coach data from the response structure
        const coachData = coachResponse.data.data || coachResponse.data;
        setCoach(coachData);

        // Fetch titles to see if coach has won any
        const titlesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/titles`);
        const titles = titlesResponse.data.filter(title => 
          title.winningCoaches?.some(coach => coach.id === parseInt(id))
        );
        setCoachTitles(titles);

        // Get current user from localStorage
        const userId = localStorage.getItem('userId');
        if (userId) {
          setCurrentUser({ userId: parseInt(userId) });
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching coach data:', error);
        setLoading(false);
      }
    };

    fetchCoachData();
  }, [id]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-900 relative">
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
            style={{ 
              backgroundImage: "url('https://t4.ftcdn.net/jpg/06/19/39/53/360_F_619395337_xfoBmAMfDhh0Wg5NRwVUUsZxJrUq8xBn.jpg')",
              zIndex: 0 
            }}
          />
          
          {/* Content Container */}
          <div className="relative z-10 container mx-auto px-4 py-8 min-h-screen">
            <LoadingAnimation type="club-banner" />
          </div>
        </div>
      </>
    );
  }

  if (!coach) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Coach Not Found</h1>
            <button 
              onClick={() => navigate('/clubs')}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg transition-colors duration-200"
            >
              Back to Clubs
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-900 relative">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
          style={{ 
            backgroundImage: "url('https://t4.ftcdn.net/jpg/06/19/39/53/360_F_619395337_xfoBmAMfDhh0Wg5NRwVUUsZxJrUq8xBn.jpg')",
            zIndex: 0 
          }}
        />
        
        {/* Content Container */}
        <div className="relative z-10 container mx-auto px-4 py-8 min-h-screen">
          {/* Back Button */}
          <div className="mb-6 pt-20">
            <button 
              onClick={() => navigate('/clubs')}
              className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back to Clubs</span>
            </button>
          </div>

          {/* Coach Profile Banner */}
          <div className="bg-gray-900 rounded-2xl shadow-2xl overflow-hidden mb-12">
            <div className="p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
              <div className="w-32 h-32 md:w-48 md:h-48 flex-shrink-0">
                <img
                  src={coach.profilePicture || '/placeholder-club.png'}
                  alt={`${coach.firstName} ${coach.lastName}`}
                  className="w-full h-full object-cover rounded-full bg-gray-700 p-2"
                />
              </div>
              <div className="flex-grow text-center md:text-left">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                  <h1 className="text-4xl md:text-5xl font-bold text-white">
                    {coach.firstName} {coach.lastName}
                  </h1>
                </div>
                
                {/* Basic Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300 mb-6">
                  <p className="flex items-center space-x-2">
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="text-red-500 font-semibold">Email:</span>
                    <span>{coach.email}</span>
                  </p>
                  {coach.phoneNumber && (
                    <p className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span className="text-red-500 font-semibold">Phone:</span>
                      <span>{coach.phoneNumber}</span>
                    </p>
                  )}
                  {coach.dateOfBirth && (
                    <p className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-red-500 font-semibold">Date of Birth:</span>
                      <span>{format(new Date(coach.dateOfBirth), 'MMM dd, yyyy')}</span>
                    </p>
                  )}
                  {coach.nationality && (
                    <p className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                      <span className="text-red-500 font-semibold">Nationality:</span>
                      <span>{coach.nationality}</span>
                    </p>
                  )}
                  <p className="flex items-center space-x-2">
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-red-500 font-semibold">Status:</span>
                    <span className={`capitalize ${coach.status === 'active' ? 'text-green-400' : 'text-yellow-400'}`}>
                      {coach.status}
                    </span>
                  </p>
                </div>

                {/* Coach Titles Section */}
                <div className="mt-6 bg-gray-800/50 p-4 rounded-lg backdrop-blur-sm">
                  <span className="text-red-500 font-semibold text-lg">Coach Achievements:</span>
                  {coachTitles.length > 0 ? (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {coachTitles.map((title) => (
                        <div key={title.titleId} className="bg-gray-700/50 p-3 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="text-white font-medium">{title.name}</h4>
                              <p className="text-gray-300 text-sm">{title.competition}</p>
                              <p className="text-gray-400 text-sm">Season: {title.season}</p>
                            </div>
                            <span className="text-gray-400 text-sm">
                              {format(new Date(title.dateWon), 'MMM dd, yyyy')}
                            </span>
                          </div>
                          {title.winningClub && (
                            <p className="text-gray-400 text-sm mt-1">
                              with {title.winningClub.name}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 text-center py-6">
                      <div className="text-gray-400 text-lg mb-2">🏆</div>
                      <p className="text-gray-400">No achievements recorded yet</p>
                      <p className="text-gray-500 text-sm mt-1">The coach is building their legacy</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Details Section */}
          <div className="bg-gray-900 backdrop-blur-sm rounded-xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Coach Profile</h2>
            <div className="space-y-6">
              {/* Bio Section */}
              <div className="bg-gray-800/50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-white mb-3">About</h3>
                <p className="text-gray-300 leading-relaxed">
                  {coach.bio || `Coach ${coach.firstName} ${coach.lastName} is a dedicated sports professional with a passion for developing talent and achieving excellence in the field.`}
                </p>
              </div>

              {/* Contact Information */}
              <div className="bg-gray-800/50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-white mb-3">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="text-gray-300">{coach.email}</span>
                  </div>
                  {coach.phoneNumber && (
                    <div className="flex items-center space-x-3">
                      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span className="text-gray-300">{coach.phoneNumber}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Professional Details */}
              <div className="bg-gray-800/50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-white mb-3">Professional Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {coach.dateOfBirth && (
                    <div>
                      <span className="text-red-500 font-semibold">Date of Birth:</span>
                      <p className="text-gray-300 mt-1">{format(new Date(coach.dateOfBirth), 'MMMM dd, yyyy')}</p>
                    </div>
                  )}
                  {coach.nationality && (
                    <div>
                      <span className="text-red-500 font-semibold">Nationality:</span>
                      <p className="text-gray-300 mt-1">{coach.nationality}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-red-500 font-semibold">Status:</span>
                    <p className={`text-gray-300 mt-1 capitalize ${coach.status === 'active' ? 'text-green-400' : 'text-yellow-400'}`}>
                      {coach.status}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
} 